import Utils from "../../src/utils/Utils";
import { BaseModel } from "./BaseModel";
import ISpinData, {
    IOverlayData,
    IReSpinData,
    ISpinificationData,
    IWinData,
    OverlayType,
} from "../server/data/ISpinData";
import { IBonusData, ICustomData } from "../server/data/ICustomData";
import SpinTypeEnum from "../const/SpinTypesEnum";
import GameModel from "./GameModel";
import gameModel from "./GameModel";
import App from "../../../core/src/App";
import betModel from "./BetModel";
import SymbolMap from "../../src/utils/SymbolMap";
import ReelGroup from "../views/ReelGroup";
import ViewManager from "../utils/ViewManager";
import { DefaultLayers } from "../utils/Layers";
import GameEvents from "../const/GameEvents";
import ConsoleUtil from "../utils/ConsoleUtil";
// import { ICascadeReelData } from "../../../../game/src/server/data/IPlayData";

class SpinModel extends BaseModel<ISpinData> {
    public featureMode: number = -1;
    public freespinSelectScreen: boolean = null;
    public winAmount: number = 0;
    public currentWin: number = 0;
    public accumulatedWin: number = 0;
    public tempWinAmount: number = 0; // used for counting of current win in cascades
    public reelHeight: number = 3;
    public spinEnded: boolean = true;
    public freespinsNum: number = 0;
    public totalFreeSpins: number = 0;
    public freespinsWon: number = 0;
    public freeSpinRetriggered: number = 0;
    public roundType: SpinTypeEnum | string = "";
    public freespinsFlags: { [flagName: string]: boolean } = {};
    public overlays: IOverlayData[] = [];
    public bonuses: IBonusData[] = [];
    public reelStopSymbols: number[][] = [];
    public reelFinalSymbols: number[][] = [];
    public reelSets: any[][][] = [];
    public reelSetIndex: number = 0;
    public stopPositions: number[] = [];
    public currentPositions: number[] = [];
    public spinification: ISpinificationData = null;
    public winsData: IWinData[] = [];
    public winsMultiplier?: number = 1;
    public winsWildMultiplier?: number = 1;
    public scatterWin: number = 0;
    public isFree: boolean = false;
    public freespinsRunning: boolean = false;
    public respinsRunning: boolean = false;
    public freegamesRunning: boolean = false;
    public freespinWinnings: number = 0;
    public stopImmediately: boolean;
    // public spinPayload: any = [];
    public spinFinished: boolean = true;
    public fsRetriggerFlag: boolean = false;
    public generations: any[] = [];
    public freespins: any = null;
    public currentGeneration: number = 0;
    public bonusWin: boolean = false;
    public firstWildIndex = null;
    public wildVersion = null;
    public firstAndThird: boolean;
    public roundFinish = false;
    public symbolReplacements?:
        | { [original: string]: any }
        | { [original: number]: any } = {};

    // Cascading properties
    public cascades: SpinModel[] = [];
    public cascadeIndex: number = -1;
    public cascadeWinOffsets: number[] = [];
    /**
     * choose your freespins countern patern and add it to the game-config
     * 9/10   - `${current}/${total}`
     * 1/10   - `${total - current}/${total}`
     *  9     - `${current}`
     *  1     - `${total - current}`
     */
    private freespinCounterPattern: string;

    // Token properties
    public tokensCount = {};

    // ReSpin data
    reSpinData?: IReSpinData = {
        awarded: 0,
        reTriggered: 0,
        win: 0,
        remaining: 0
    };

    tempSymbols: any;

    public hasOverlay(type?: OverlayType): boolean {
        let currentOverlays = this.getCurrentSpinProperty("overlays");
        let hasOverlays = Boolean(currentOverlays && currentOverlays.length);
        if (type && hasOverlays) {
            hasOverlays = false;
            currentOverlays.forEach((overlay) => {
                if (overlay.type === type) {
                    hasOverlays = true;
                }
            });
        }
        return hasOverlays;
    }

    public hasReSpins(): boolean {
        return (
            this.reSpinData &&
            this.reSpinData.awarded > 0 &&
            this.reSpinData.remaining > 0
        );
    }

    public getCurrentWinsData() {
        return this.getCurrentSpinProperty("winsData");
    }

    public getCurrentCascadeWinMap() {
        let { numVisibleLines, numReels } = GameModel.gameConfig;
        let markedArr = [];
        let winOffsets = this.getCurrentSpinProperty("cascadeWinOffsets");

        // Non-winning spins have no win offsets - so check for it
        if (winOffsets && winOffsets.length != 0) {
            for (let reelIndex = 0; reelIndex < numReels; reelIndex++) {
                markedArr[reelIndex] = [];
                for (let rowIndex = 0; rowIndex < numVisibleLines; rowIndex++) {
                    markedArr[reelIndex][rowIndex] = winOffsets.includes(
                        rowIndex * numReels + reelIndex
                    );
                }
            }
        }

        return markedArr;
    }

    public getCurrentCascadeExplodeMap() {
        let { numVisibleLines, numReels } = GameModel.gameConfig;
        let markedArr = [];
        let explodesOffsets = this.getCurrentSpinProperty("cascadeExplodesOffsets");

        // Non-winning spins have no win offsets - so check for it
        if (explodesOffsets && explodesOffsets.length != 0) {
            for (let reelIndex = 0; reelIndex < numReels; reelIndex++) {
                markedArr[reelIndex] = [];
                for (let rowIndex = 0; rowIndex < numVisibleLines; rowIndex++) {
                    markedArr[reelIndex][rowIndex] = explodesOffsets.includes(
                        rowIndex * numReels + reelIndex
                    );
                }
            }
        }

        return markedArr.length ? markedArr : this.getCurrentCascadeWinMap();
    }

    public getOverlay(type: OverlayType): IOverlayData  {
        let currentOverlays = this.getCurrentSpinProperty("overlays");
        for (let overlayId in currentOverlays) {
            if (currentOverlays[overlayId].type === type) {
                return Utils.deepClone(currentOverlays[overlayId]);
            }
        }
        return null;
    }

    public getOverlays(type: OverlayType): IOverlayData[] {
        let currentOverlays = this.getCurrentSpinProperty("overlays");
        let overlays: IOverlayData[]  = [];
        for (let overlayId in currentOverlays) {
            if (currentOverlays[overlayId].type === type) {
                overlays.push(Utils.deepClone(currentOverlays[overlayId])) ;
            }
        }
        return overlays;
    }

    public hasScatterWin(): boolean {
        return this.scatterWin > 0;
    }

    // public resolveSpinPayload() {
    //     if (Array.isArray(this.spinPayload)) {
    //         Utils.serialize(this.spinPayload.shift(), this);
    //     } else {
    //         Utils.serialize(this.spinPayload, this);
    //     }
    // }

    update(data?: any) {
        let oldData = Utils.deepClone(this.processedData);
        super.update(data);
        // let processedData = this.processData(data);
        // if (processedData.length || data) {
        if (
            (oldData != this.processedData && this.processedData.length) ||
            data
        ) {
            this.roundFinish = false;
        }

        this.updateCounters();
    }

    /**
     * Ensures the reelsets will have the exact final symbols,
     * when they stop. This method modifies the reelsets if needed.
     *
     * @param {number} reelIdx Zero-based reel index.
     * @memberof SpinModel
     */
    public applyStackReplacement(reelIdx: number) {
        const reelgroup = ViewManager.getLayer(DefaultLayers.Reels)?.getCurrentScene()?.getSubView("ReelGroup") as ReelGroup;
        if (!reelgroup) {
            return;
        }

        const idsToSkip = [reelgroup.emptySym];
        if (App.config.skipStackReplacement) {
            (App.config.skipStackReplacement as number[]).forEach(id => {
                idsToSkip.push(id);
            });
        }

        this.stopPositions.forEach((stopPos, reelIndex) => {
            if (reelIndex == reelIdx) {
                for (let rowIndex = 0; rowIndex < this.reelFinalSymbols[reelIdx].length; rowIndex++) {
                    const reelSet = this.reelSets[this.reelSetIndex][reelIndex];
                    const position = (stopPos + rowIndex) % reelSet.length;
                    const symbolId = SymbolMap.getSymbolId(reelSet[position]);
                    if (symbolId == reelgroup.emptySym) {
                        continue;
                    }

                    const finalId = this.reelFinalSymbols[reelIndex][rowIndex];

                    if (idsToSkip.includes(finalId)) {
                        ConsoleUtil.orange(`[Stack Replacement SKIP] Reel: ${reelIdx}, ReelSet pos: ${position}, Old id: ${symbolId}, New id: ${finalId}`);
                        continue;
                    }

                    if (symbolId != finalId) {
                        // Applying the symbol substitution
                        ConsoleUtil.orange(`[Stack Replacement] Reel: ${reelIdx}, ReelSet pos: ${position}, Old id: ${symbolId}, New id: ${finalId}`);
                        reelSet[position] = finalId;
                    }
                }
            }
        })
    }

    protected updateCounters() {
        if (ViewManager.hasLayer(DefaultLayers.FreespinCounter)) {
            if (this.freespinsRunning) {
                if (!this.respinsRunning) {
                    let current = this.freespinsNum;
                    let total = this.totalFreeSpins;
                    this.freespinCounterPattern = App.config.fsCounterPattern || "`${current}/${total}`";
                    const textField = ViewManager.getLayer(DefaultLayers.FreespinCounter).setScene("open")?.sceneTextFields.get("fs_counter");
                    if (textField) {
                        textField.text = eval(this.freespinCounterPattern);
                    }
                } else {
                    ViewManager.getLayer(DefaultLayers.FreespinCounter)?.completeClear();
                }
            } else {
                ViewManager.getLayer(DefaultLayers.FreespinCounter).completeClear();
            }
        }


        if (ViewManager.hasLayer(DefaultLayers.RespinCounter)) {
            if (this.respinsRunning) {
                const initialOpening = ViewManager.getLayer(DefaultLayers.RespinCounter).getCurrentScene() == null;
                const textField = ViewManager.getLayer(DefaultLayers.RespinCounter).setScene("open")?.sceneTextFields.get("rs_counter");
                if (textField) {
                    if (initialOpening && !this.reSpinData.remaining) {
                        textField.text = `${this.reSpinData.awarded}/${this.reSpinData.awarded}`;
                    } else {
                        textField.text = `${this.reSpinData.remaining}/${this.reSpinData.awarded}`;
                    }
                }
            } else {
                ViewManager.getLayer(DefaultLayers.RespinCounter).completeClear();
            }
        }

        if (this.freespinsRunning || this.respinsRunning) {
            App.dispatcher.emit(GameEvents.COUNTERS_UPDATED);
        }
    }

    public updateFreespinWinnings() {
        this.freespinWinnings += this.getCurrentSpinProperty("currentWin");
    }

    public hasResultForReel(reelIdx: number): boolean {
        return (
            this.reelStopSymbols[reelIdx] != undefined &&
            this.reelStopSymbols[reelIdx].length > 0
        );
    }

    public getStopSymbolsForReels(
        reelIdxs: number[],
        setIndex?: number
    ): any[] {
        let result = [];

        reelIdxs.forEach((reel, index) => {
            result[index] = this.getStopSymsForReel(reel, setIndex);
        });

        return result;
    }

    private getCurrentStopPositions(reelIdx) {
        // The current stop positions
        let currentStopPositions =
            this.getCurrentSpinProperty("stopPositions")?.[reelIdx];

        return currentStopPositions != null && currentStopPositions != undefined
            ? currentStopPositions
            : GameModel.gameConfig.defaultStopPositions[reelIdx];
    }

    public getCurrentWinsMultiplier() {
        return this.getCurrentSpinProperty("winsMultiplier");
    }

    public getAccumulatedWin() {
        return this.getCurrentSpinProperty("accumulatedWin");
    }

    public getCurrentWin() {
        let win =
            // If there is a cascade there will be currentWin and accumulatedWin
            this.getCurrentSpinProperty("currentWin") ||
            // If it's a regular spin - maybe we have just winAmount
            this.getCurrentSpinProperty("winAmount")
        ;


        return win;
    }


    public getRespinTotalWon(spinData: any): number {
        if (!spinData.bonusInfo || spinData.bonusInfo.length == 0) {
            return 0;
        }

        let totalWon = 0;
        spinData.bonusInfo.forEach(bonus => {
            if (bonus.reSpinsList && bonus.reSpinsList.length) {
                totalWon += bonus.creditsWon || 0;
            }
        });

        return totalWon;
    }

    public getCurrentSpinProperty(property: keyof ISpinData) {
        if (
            this.cascades &&
            this.cascadeIndex != -1 &&
            this.cascades.length > 0 &&
            this.cascadeIndex < this.cascades.length
        ) {
            // If there are cascades to be shown - get the prop from there

            return this.cascades[this.cascadeIndex][property];
        } else {
            // If not - get it from the current spin model
            return this[property];
        }
    }

    public getNextSpinProperty(property: keyof ISpinData) {
        if (
            this.cascades &&
            this.cascadeIndex != -1 &&
            this.cascades.length > 0 &&
            this.cascadeIndex + 1 < this.cascades.length
        ) {
            // If there are cascades to be shown - get the prop from there

            return this.cascades[this.cascadeIndex + 1][property];
        } else {
            // If not - get it from the current spin model
            return this[property];
        }
    }

    public getStopSymsForReel(
        reelIdx: number,
        setIndex?: number,
        reelExtraSymbols?: number,
        numSyms?: number
    ) {
        const extraSymbols = isNaN(reelExtraSymbols)
            ? GameModel.gameConfig.reelExtraSymbols || 0
            : reelExtraSymbols;

        if (!numSyms) {
            numSyms = this.reelStopSymbols[reelIdx]
                ? this.reelStopSymbols[reelIdx].length
                : this.getSymsForReel(reelIdx);
        }

        const stopPosition = this.getCurrentStopPositions(reelIdx);

        return this.getNumberSymbolsAfterStop(
            reelIdx,
            stopPosition - extraSymbols,
            numSyms + extraSymbols * 2,
            setIndex
        );
    }

    public getFinalSymsForReels(reelIdxs: number[]): any[] {
        let result = [];
        reelIdxs.forEach((reel, index) => {
            result[index] = this.getFinalSymsForReel(reel);
        });

        return result;
    }

    public getCurrentFinalSymbols() {
        return this.getCurrentSpinProperty("reelFinalSymbols") as number[][];
    }

    public getFinalSymsForReel(reelIdx: number) {
        return this.reelFinalSymbols[reelIdx];
    }

    public getNextStopPosition(
        reelIdx: number,
        setIndex?: number,
        offsetFromStop?: number
    ): number {
        setIndex =
            setIndex != null && setIndex != undefined
                ? setIndex
                : this.reelSetIndex;

        let stopPosition: number;

        if (this.currentPositions[reelIdx] != undefined) {
            stopPosition = this.currentPositions[reelIdx];
        } else if (this.stopPositions[reelIdx] != undefined) {
            stopPosition = this.stopPositions[reelIdx] - (offsetFromStop || 0);
        } else if (GameModel.gameConfig.defaultStopPositions[reelIdx]) {
            stopPosition = GameModel.gameConfig.defaultStopPositions[reelIdx];
        }

        if (stopPosition == undefined || isNaN(stopPosition)) {
            stopPosition = 0;
        }

        stopPosition++;
        if (stopPosition > this.reelSets[setIndex][reelIdx].length - 1) {
            stopPosition =
                stopPosition - this.reelSets[setIndex][reelIdx].length;
        }

        const syms = SymbolMap.parseSymbols(
            this.getNumberSymbolsAfterStop(reelIdx, stopPosition, 1, setIndex)
        );
        this.currentPositions[reelIdx] = stopPosition;

        return syms[0];
    }

    public getPreviousStopPosition(
        reelIdx: number,
        setIndex?: number,
        offsetFromStop?: number
    ): number {
        setIndex =
            setIndex != null && setIndex != undefined
                ? setIndex
                : this.reelSetIndex;

        let stopPosition: number;

        if (this.currentPositions[reelIdx] != undefined) {
            stopPosition = this.currentPositions[reelIdx];
        } else if (this.stopPositions[reelIdx] != undefined) {
            stopPosition = this.stopPositions[reelIdx] + (offsetFromStop || 0);
        } else if (GameModel.gameConfig.defaultStopPositions[reelIdx]) {
            stopPosition = GameModel.gameConfig.defaultStopPositions[reelIdx];
        }

        if (stopPosition == undefined || isNaN(stopPosition)) {
            stopPosition = this.reelSets[setIndex][reelIdx].length - 1;
        }

        stopPosition--;
        if (stopPosition < 0) {
            stopPosition =
                this.reelSets[setIndex][reelIdx].length + stopPosition;
        }
        // debugger;
        const syms = SymbolMap.parseSymbols(
            this.getNumberSymbolsAfterStop(reelIdx, stopPosition, 1, setIndex)
        );
        this.currentPositions[reelIdx] = stopPosition;

        return syms[0];
    }

    public resetCurrentStopsPerReel(reelIdx) {
        this.currentPositions[reelIdx] = undefined;
    }

    public getNumberSymbolsAfterStop(
        reelIdx: number,
        stopPosition: number,
        count: number,
        setIndex?: number
    ): any[] {
        setIndex =
            setIndex != null && setIndex != undefined
                ? setIndex
                : this.reelSetIndex;
        const finalSyms = [];

        if (this.reelSets.length > 0) {
            if (!(this.reelSets[setIndex][reelIdx].length === 0)) {
                while (
                    stopPosition >= this.reelSets[setIndex][reelIdx].length ||
                    stopPosition < 0
                ) {
                    if (stopPosition < 0) {
                        stopPosition =
                            this.reelSets[setIndex][reelIdx].length +
                            stopPosition;
                    }
                    if (
                        stopPosition >= this.reelSets[setIndex][reelIdx].length
                    ) {
                        stopPosition =
                            stopPosition -
                            this.reelSets[setIndex][reelIdx].length;
                    }
                }
            }
        }

        for (let symIdx = 0; symIdx < count; symIdx++) {
            // TODO initial symbools set
            let sym = 1;
            if (
                this.reelSets.length != 0 &&
                this.reelSets[setIndex][reelIdx] &&
                this.reelSets[setIndex][reelIdx].length
            ) {
                let index = stopPosition + symIdx;
                if (index > this.reelSets[setIndex][reelIdx].length - 1) {
                    index = index - this.reelSets[setIndex][reelIdx].length;
                }
                let id = this.reelSets[setIndex][reelIdx][index];
                sym = SymbolMap.parseSymbols([id])[0]; // TODO FIX MAPING
            } else {
                sym = this.getRandomSymId(reelIdx);
            }

            finalSyms.push(sym);
        }

        return finalSyms;
    }

    public getSymsForReel(reelIdx: number): number {
        return gameModel.gameConfig.layout[reelIdx];
    }

    // TODO hold logic
    public getPlayerHoldsPattern() {
        return [false, false, false, false, false];
    }

    // TODO add reelSet support
    public getRandomSymId(reelId = 0, restrict = false) {
        const symIds = App.config.symbolIds;

        let rndId = symIds[Math.floor(Math.random() * symIds.length)];

        if (restrict == true && App.config.restrictedSymbols) {
            while (App.config.restrictedSymbols.indexOf(rndId) != -1) {
                rndId = symIds[Math.floor(Math.random() * symIds.length)];
            }
        }

        return rndId;
    }

    public setReelSetIndex(index: number) {
        this.reelSetIndex = index;
    }

    public enterFreespins() {
        this.freespinsRunning = true;
        // if (!this.fsRetriggerFlag) {
        //     // Set the initial FS win to the triggering spin win
        //     this.freespinWinnings = this.currentWin
        // }
        // this.freespinsToShow = this.freespinsNum;
        // this.fsRetriggerFlag = true;
        this.updateCounters();
    }

    public exitFreespins() {
        this.freespinsFlags = {};
        this.freespinsRunning = false;
        this.fsRetriggerFlag = false;
        this.freespinWinnings = 0;
        this.freespinSelectScreen = null;
        this.updateCounters();
    }

    public isInFreespins() {
        return this.freespinsRunning;
    }

    public isInRespins() {
        return this.respinsRunning;
    }

    public enterFreegames() {
        this.freegamesRunning = true;
    }

    public exitFreegames() {
        this.freegamesRunning = false;
    }

    public inFreegames(): boolean {
        return this.freegamesRunning;
    }

    public enterRespins() {
        this.respinsRunning = true;
        this.updateCounters();
    }

    public exitRespins() {
        this.respinsRunning = false;
        this.updateCounters();
    }

    public hasWinnings(): boolean {
        // If there is a cascade - check where we are
        if (this.cascades && this.cascades.length > 0) {
            // If we're still in the cascades - check the cascade win
            if (this.cascadeIndex < this.cascades.length) {
                return this.getCurrentSpinProperty("winAmount") > 0;
            } else {
                // Else the cascade is over and the main winAmount is the accumulated total - so no spin win
                return false;
            }
        } else {
            // Otherwise - check the main winAmount
            return this.winAmount > 0;
        }
    }

    public hasBigWin(): boolean {
        return (
            this.getWinRatio() >=
            (GameModel.gameConfig as any).bigWinRatioLevels[0]
        );
    }

    public hasFreespinsWon(): boolean {
        return this.freespinsWon > 0;
    }

    public hasRespinsWon(): boolean {
        if (!this.reSpinData) {
            return false;
        }

        if (this.respinsRunning) {
            return false;
        }

        if (this.reSpinData.awarded == 0) {
            return false;
        }

        return this.reSpinData.awarded == this.reSpinData.remaining;
    }

    public hasRespinsRetriggered(): boolean {
        if (!this.reSpinData) {
            return false;
        }

        if (this.reSpinData.awarded == 0) {
            return false;
        }

        return this.respinsRunning && this.reSpinData.awarded == this.reSpinData.remaining;
    }

    public hasFreespins(): boolean {
        return this.freespinsNum > 0 || this.freespinsWon > 0;
    }

    public hasFreespinSelectScreen(): boolean {
        return this.freespinSelectScreen;
    }

    public hasRemovedSymbols(): boolean {
        return this.generations.length > 1;
    }

    //TODO - this is duplicated
    public getRemovedSymbols() {
        const respinReels: boolean[] = new Array(5).fill(false);
        const removedCoords =
            this.generations[this.currentGeneration].removedSymbols;
        const removedCoordsLen = removedCoords.length;
        for (let i = 0; i < removedCoordsLen; i++) {
            const reelIndex = removedCoords[i].x - 1;
            respinReels[reelIndex] = true;
        }
        return respinReels;
    }

    public getCurrentGenSymbols() {
        const genSymbols =
            this.generations[this.currentGeneration]?.symbols || [];
        return genSymbols;
    }

    public hasNextGeneration() {
        return this.currentGeneration < this.generations.length - 1;
    }

    public getNextGenSymbols() {
        if (this.hasNextGeneration()) {
            this.currentGeneration++;
        }
        const genSymbols =
            this.generations[this.currentGeneration]?.symbols || [];

        return genSymbols;
    }

    public getWinningSymbols() {
        const winningSymbols = this.createSymArr(
            (ViewManager.getLayer(DefaultLayers.Reels).getCurrentScene().getSubView("ReelGroup") as ReelGroup).reels.length,
            gameModel.gameConfig.symbolCount,
            false
        );
        // coordinates are starting from 1 and because of that every calculation is made with -1
        let winsData = this.getCurrentSpinProperty("winsData");
        for (let i = 0; i < winsData.length; i++) {
            this.resolveWinCoords(winsData[i].coordinates, winningSymbols);
        }
        return winningSymbols;
    }

    public getWinRatio() {
        return this.winAmount / betModel.totalBet;
    }

    getWinsoundName() {
        const winSoundRatios = GameModel.gameConfig.winSoundRatios;
        const currentWinRatio = this.getWinRatio();
        let soundName;
        for (let i = winSoundRatios.length - 1; i >= 0; i--) {
            if (winSoundRatios[i].winRatio <= currentWinRatio) {
                soundName = winSoundRatios[i].sound;
                break;
            }
        }
        return soundName;
    }

    public generateReelsets() {
        if (this.reelSets.length != 0) {
            return;
        }
        for (let i = 0; i < 2; i++) {
            this.reelSets[i] = this.createSymArr(
                GameModel.gameConfig.numReels,
                25,
                null,
                {
                    min: GameModel.gameConfig.symbolIds[0],
                    max: GameModel.gameConfig.symbolIds[
                        GameModel.gameConfig.symbolIds.length - 1
                    ],
                }
            );
        }
    }

    public insertResultInReelset() {
        let reelset;
        if (this.freespinsRunning) {
            reelset = this.reelSets[1];
        } else {
            reelset = this.reelSets[0];
        }

        for (let i = 0; i < this.reelStopSymbols.length; i++) {
            for (let j = 0; j < this.reelStopSymbols[i].length; j++) {
                const reelStopSymbolElement = this.reelStopSymbols[i][j];
                reelset[i][j] = reelStopSymbolElement;
            }
        }
    }

    private resolveWinCoords(
        coordinatesArr: { x: number; y: number }[],
        resultArr
    ) {
        for (let i = 0; i < coordinatesArr.length; i++) {
            const currentCoordinate = coordinatesArr[i];
            const reelIndex = currentCoordinate.x - 1;
            const symbolIndex = currentCoordinate.y - 1;
            resultArr[reelIndex][symbolIndex] = true;
        }
        return resultArr;
    }

    /**
     * Creates a matrix of symbols with given parameters
     * @param col
     * @param row
     * @param emptyVal - empty value to be inserted. Defaults to 0
     * @param random - whether a random number should be drawn for each position
     */
    public createSymArr(
        col,
        row,
        emptyVal: any = 0,
        random?: { min: number; max: number }
    ) {
        const result = [];
        for (let i = 0; i < col; i++) {
            result[i] = [];
            for (let j = 0; j < row; j++) {
                if (random) {
                    const min = Math.ceil(random.min);
                    const max = Math.floor(random.max);
                    result[i][j] =
                        Math.floor(Math.random() * (max - min + 1)) + min;
                } else {
                    result[i][j] = emptyVal;
                }
            }
        }
        return result;
    }
}

export default new SpinModel();
